<template>
  <div
    v-if="currentQuestionData"
    :class="[
      style.listOneAnswerSurface,
      currentQuestionData?.vertical ? style.doubleColumns : null,
      mobileVersion ? style.mobile : null
    ]"
  >
    <TransitionGroupFromTop>
      <InputButtonElementQuestion
        v-for="(variant, index) in currentVariants"
        :key="getVariantKey(variant, index)"
        :variant="variant"
        :index="index"
        :deleteFunction="deleteVariant"
        :setTextFunction="setVariantText"
        :withImg="withImg"
      />
    </TransitionGroupFromTop>
  </div>
  <ButtonAdd :class="style.buttonAdd" :text="'Добавить'" @click="addVariant" />
</template>

<script lang="ts">
import {defineComponent} from "vue";

import style from "./ListAnswers.module.scss";

import ButtonAdd from "@/elements/buttonAdd/ButtonAdd.vue";
import InputButtonElementQuestion from "@/elements/quizElements/InputButtonElementQuestion/InputButtonElementQuestion.vue";
import TransitionGroupFromTop from "@/dynamics/transitions/TransitionGroupFromTop/TransitionGroupFromTop.vue";

import {modifyQuiz} from "@/page/quiz/scriptQuiz";
import {questions, QuestionVariant} from "@/page/quiz/typeQuiz";

export default defineComponent({
  name: "ListAnswers",

  components: {
    InputButtonElementQuestion,
    ButtonAdd,
    TransitionGroupFromTop
  },

  data() {
    return {
      style,
      modifyQuiz,
      variantKeyMap: new WeakMap<QuestionVariant, string>()
    };
  },

  props: {
    withImg: {
      type: Boolean,
      default: false,
      required: false
    }
  },

  methods: {
    createVariantKey(index: number): string {
      return `answer_variant_${this.selectedQuestion}_${index}_${Date.now()}_${Math.random()
        .toString(16)
        .slice(2)}`;
    },

    getCurrentQuestionWithVariants(): questions | null {
      if (!this.currentQuestionData) {
        return null;
      }

      return this.currentQuestionData as questions;
    },

    getVariantKey(variant: QuestionVariant, index: number): string {
      if (variant._key) {
        return variant._key;
      }

      const existingKey = this.variantKeyMap.get(variant);

      if (existingKey) {
        return existingKey;
      }

      const newKey = this.createVariantKey(index);

      this.variantKeyMap.set(variant, newKey);

      return newKey;
    },

    deleteVariant(index: number): void {
      const question = this.getCurrentQuestionWithVariants();

      if (!question || !Array.isArray(question.variants)) {
        return;
      }

      question.variants.splice(index, 1);
    },

    addVariant(): void {
      const question = this.getCurrentQuestionWithVariants();

      if (!question) {
        return;
      }

      if (!Array.isArray(question.variants)) {
        question.variants = [];
      }

      question.variants.push({
        img: "",
        text: "",
        _key: this.createVariantKey(question.variants.length)
      });
    },

    setVariantText(event: InputEvent, index: number): void {
      const target = event.target as HTMLInputElement | null;

      if (!target) {
        return;
      }

      const question = this.getCurrentQuestionWithVariants();

      if (!question || !Array.isArray(question.variants)) {
        return;
      }

      if (!question.variants[index]) {
        return;
      }

      question.variants[index].text = target.value;
    }
  },

  computed: {
    quiz() {
      return this.$quizStore.getQuiz;
    },

    selectedQuestion(): number {
      return this.$quizStore.getSelectedQuestion;
    },

    selectedVersion(): string {
      return this.$quizStore.getSelectedVersion;
    },

    mobileVersion(): boolean {
      return this.selectedVersion === "Мобильная";
    },

    currentQuestionData(): questions | null {
      if (!this.quiz?.questions) {
        return null;
      }

      if (this.selectedQuestion < 0) {
        return null;
      }

      return this.quiz.questions[this.selectedQuestion] || null;
    },

    currentVariants(): QuestionVariant[] {
      if (!Array.isArray(this.currentQuestionData?.variants)) {
        return [];
      }

      return this.currentQuestionData.variants;
    }
  }
});
</script>
